import React, { useCallback, useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/breadcrumb";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useAdminApiClient } from "../../hooks/useApiClient";
import {
  Box,
  Button,
  HStack,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Image,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  SkeletonText,
} from "@chakra-ui/react";
import { EditIcon } from "../../styles/icons";
import { useToast } from "../../hooks/useToast";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { ApplicationDetailForm } from "../../forms/ApplicationDetailForm";
import DateUtil from "../../util/DateUtil";
import { CopyText } from "../../components/Utilities/CopyText";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { PreviewButton } from "../../components/Utilities/PreviewButton";
import { ChevronUpIcon } from "@chakra-ui/icons";

interface ApplicationDetailProps {
  create?: boolean;
}

export const ApplicationDetail = (props: ApplicationDetailProps) => {
  const [searchParams] = useSearchParams();
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [application, setApplication] =
    useState<CherryHubAdminApi.PlatformApplication>();
  const { data: applicationData, isLoading } = useAdminApiRequest(
    (adminApiClient) => adminApiClient.getPlatformApplication(applicationId!),
    [applicationId]
  );
  const apiClient = useAdminApiClient();
  const { showErrorToast, showSuccessToast } = useToast();
  const [editMode, setEditMode] = useState<boolean>(
    props.create ??
      (searchParams.get("edit") !== null &&
      (searchParams.get("edit") === "" || searchParams.get("edit") == "true")
        ? true
        : false)
  );

  const onSuccess = useCallback(
    (application: CherryHubAdminApi.PlatformApplication) => {
      setApplication(application);
      setEditMode(false);
      if (props.create) {
        showSuccessToast("Application created.");
        navigate(`/admin/applications/${applicationId}`);
      } else {
        showSuccessToast("Application updated.");
      }
    },
    [showSuccessToast, application]
  );

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(message ?? "Application update failed.");
    },
    [showErrorToast]
  );

  useEffect(() => {
    setApplication(applicationData!);
  }, [applicationData]);

  return (
    <>
      <PageHeading>
        <PageHeading.Title>
          {props.create ? "Create" : "Edit"} Application
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%">
          <Breadcrumb variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to={`/admin/applications`}>
                Applications
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/applications/${
                  props.create ? "create" : application?.id
                }`}
              >
                {props.create ? "New" : application?.DisplayName}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>
          {isLoading ? (
            <VStack
              w="100%"
              p={6}
              boxShadow="lg"
              alignItems="flex-start"
              spacing={4}
            >
              <SkeletonText w="220px" noOfLines={20} spacing="4" />
              <SkeletonText w="800px" noOfLines={5} spacing="4" />
            </VStack>
          ) : editMode ? (
            <ApplicationDetailForm
              apiClient={apiClient}
              application={application!}
              onCancel={() =>
                props.create
                  ? navigate(`/admin/applications`)
                  : setEditMode(false)
              }
              onSuccess={onSuccess}
              onFailure={onFailure}
            />
          ) : (
            <VStack alignItems="flex-start" width="100%">
              <HStack w="100%">
                <Box flexGrow={2} pb={4}>
                  <CopyText text={application?.id} hover adjustMargin>
                    <Text fontSize="md" color="cherryUi.600">
                      {application?.id}
                    </Text>
                  </CopyText>
                  <Text fontSize="xl" color="cherryUi.600">
                    <b>{application?.DisplayName}</b>
                  </Text>
                </Box>
                <Box>
                  <Button
                    leftIcon={<EditIcon />}
                    onClick={() => setEditMode(true)}
                    colorScheme="cherryButton"
                  >
                    Edit
                  </Button>
                </Box>
              </HStack>
              <HStack w="100%" pb={6}>
                <VStack alignItems="flex-start" w="100%">
                  <ViewControl
                    label="Enabled"
                    valueProps={{ flex: 2 }}
                    value={application?.IsEnabled ? "Yes" : "No"}
                  />
                  <ViewControl label="Client Id">
                    <Box flex={2}>
                      <CopyText adjustMargin>{application?.ClientId}</CopyText>
                    </Box>
                  </ViewControl>
                  <ViewControl label="Home Page Url">
                    <Box flex={2}>
                      {(application?.HomePageUrl && (
                        <PreviewButton
                          url={`${application?.HomePageUrl}`}
                          adjustMargin
                        >
                          {application?.HomePageUrl}
                        </PreviewButton>
                      )) ??
                        "-"}
                    </Box>
                  </ViewControl>
                  <ViewControl label="Terms Of Service Url">
                    <Box flex={2}>
                      {(application?.TermsOfServiceUrl && (
                        <PreviewButton
                          url={`${application?.TermsOfServiceUrl}`}
                          adjustMargin
                        >
                          {application?.TermsOfServiceUrl}
                        </PreviewButton>
                      )) ??
                        "-"}
                    </Box>
                  </ViewControl>
                  <ViewControl label="Privacy Statement Url">
                    <Box flex={2}>
                      {(application?.PrivacyStatementUrl && (
                        <PreviewButton
                          url={`${application?.PrivacyStatementUrl}`}
                          adjustMargin
                        >
                          {application?.PrivacyStatementUrl}
                        </PreviewButton>
                      )) ??
                        "-"}
                    </Box>
                  </ViewControl>
                  <ViewControl label="Support Url">
                    <Box flex={2}>
                      {(application?.SupportUrl && (
                        <PreviewButton
                          url={`${application?.SupportUrl}`}
                          adjustMargin
                        >
                          {application?.SupportUrl}
                        </PreviewButton>
                      )) ??
                        "-"}
                    </Box>
                  </ViewControl>
                  <ViewControl
                    label="Publisher Domain"
                    valueProps={{ flex: 2 }}
                    value={application?.PublisherDomain}
                  />
                  <ViewControl
                    label="Publisher Business Name"
                    valueProps={{ flex: 2 }}
                    value={application?.PublisherBusinessName}
                  />
                  <ViewControl
                    label="Support Email Address"
                    valueProps={{ flex: 2 }}
                    value={application?.SupportEmailAddress}
                  />
                  <ViewControl
                    label="Publisher Primary Contact Name"
                    valueProps={{ flex: 2 }}
                    value={application?.PublisherPrimaryContactName}
                  />
                  <ViewControl
                    label="Publisher Primary Contact Telephone"
                    valueProps={{ flex: 2 }}
                    value={application?.PublisherPrimaryContactTelephone}
                  />
                  <ViewControl
                    label="Publisher Primary Contact Email Address"
                    valueProps={{ flex: 2 }}
                    value={application?.PublisherPrimaryContactEmailAddress}
                  />
                  <ViewControl
                    label="Publisher Secondary Contact Name"
                    valueProps={{ flex: 2 }}
                    value={application?.PublisherSecondaryContactName}
                  />
                  <ViewControl
                    label="Publisher Secondary Contact Telephone"
                    valueProps={{ flex: 2 }}
                    value={application?.PublisherSecondaryContactTelephone}
                  />
                  <ViewControl
                    label="Publisher Secondary Contact Email Address"
                    valueProps={{ flex: 2 }}
                    value={application?.PublisherSecondaryContactEmailAddress}
                  />
                  <ViewControl
                    label="Return Existing User On OAuth2 Authorization Code Flow"
                    valueProps={{ flex: 2 }}
                    value={
                      application?.ReturnExistingUserOnOAuth2AuthorizationCodeFlow
                        ? "Yes"
                        : "No"
                    }
                  />
                  <ViewControl
                    label="Require Client Secret On OAuth2 Token Request"
                    valueProps={{ flex: 2 }}
                    value={
                      application?.RequireClientSecretOnOAuth2TokenRequest
                        ? "Yes"
                        : "No"
                    }
                  />

                  {application?.RequireClientSecretOnOAuth2TokenRequest && (
                    <>
                      <ViewControl label="Client Secrets">
                        {(application?.ClientSecrets?.length && (
                          <Table size="sm" flex={2}>
                            <Thead>
                              <Tr>
                                <Th>Secret ID</Th>
                                <Th>Expiry Date</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {application?.ClientSecrets?.map((secret) => (
                                <Tr key={secret.SecretId}>
                                  <Td>
                                    <CopyText>{secret.SecretId}</CopyText>
                                  </Td>
                                  <Td>
                                    {DateUtil.getLocalDateFormatFromString(
                                      secret?.ExpiryDate
                                    ) ?? "-"}
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        )) || (
                          <Text fontStyle="italic" flex={2}>
                            Client secret has not been set
                          </Text>
                        )}
                      </ViewControl>
                    </>
                  )}
                  <ViewControl label="Redirect Uris">
                    <Box flex={2}>
                      {(application?.RedirectUris?.length && (
                        <UnorderedList>
                          {application?.RedirectUris?.map((uri) => (
                            <ListItem key={uri}>
                              <PreviewButton url={`${uri}`} adjustMargin>
                                {uri}
                              </PreviewButton>
                            </ListItem>
                          ))}
                        </UnorderedList>
                      )) || (
                        <Text fontStyle="italic">
                          Redirect Uris has not been set
                        </Text>
                      )}
                    </Box>
                  </ViewControl>
                  <ViewControl label="Default Permissions">
                    <Box flex={2}>
                      {(application?.DefaultPermissions?.length && (
                        <UnorderedList>
                          {application?.DefaultPermissions?.map(
                            (permission) => (
                              <ListItem key={permission}>{permission}</ListItem>
                            )
                          )}
                        </UnorderedList>
                      )) || (
                        <Text fontStyle="italic">
                          Default permissions has not been set
                        </Text>
                      )}
                    </Box>
                  </ViewControl>
                  <ViewControl label="Allowed Permissions">
                    <Box flex={2}>
                      {(application?.AllowedPermissions?.length && (
                        <UnorderedList>
                          {application?.AllowedPermissions?.map(
                            (permission) => (
                              <ListItem ml={6} key={permission}>
                                {permission}
                              </ListItem>
                            )
                          )}
                        </UnorderedList>
                      )) || (
                        <Text fontStyle="italic">
                          Allowed permissions has not been set
                        </Text>
                      )}
                    </Box>
                  </ViewControl>
                  <Accordion
                    w="100%"
                    allowToggle
                    allowMultiple
                    variant="section"
                  >
                    <AccordionItem>
                      {({ isExpanded }) => (
                        <>
                          <h2>
                            <AccordionButton>
                              <Box flex="1" textAlign="left">
                                <b>Data Store</b>
                              </Box>
                              {isExpanded ? (
                                <ChevronUpIcon color="white" fontSize="xl" />
                              ) : (
                                <AccordionIcon />
                              )}
                            </AccordionButton>
                          </h2>
                          <AccordionPanel width="100%">
                            <pre>
                              {application?.DataStore == null
                                ? ""
                                : JSON.stringify(
                                    application?.DataStore,
                                    null,
                                    2
                                  )}
                            </pre>
                          </AccordionPanel>
                        </>
                      )}
                    </AccordionItem>
                  </Accordion>
                </VStack>

                {application?.LogoUrl && (
                  <Box alignSelf="flex-start">
                    <Image
                      maxHeight="200px"
                      objectFit="contain"
                      src={application?.LogoUrl}
                    />
                  </Box>
                )}
              </HStack>
            </VStack>
          )}
        </VStack>
      </PageContent>
    </>
  );
};
